<script setup lang="ts">
import type { ReportFilters } from '~/models/ReportFilters'
import type { ContentDetailsAssignment, ContentDetailsTest, ReportContentVisit } from '~/models/Report'
import type { GroupMember } from '~/models/Group'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons'
import { PendoFeature } from '~/models/Pendo'
import Button from '~/components/ui/Button.vue'
import TaskDetail from '~/components/report/TaskDetail.vue'
import AssignmentDetail from '~/components/report/AssignmentDetail.vue'

type ActivityDetailSection = 'main' | 'task' | 'assignment'
type ActivityDetailRelation = ContentDetailsTest | ContentDetailsAssignment | null

const props = defineProps<{
  content: BaseItem
  visit?: ReportContentVisit
  filters?: ReportFilters
  activeSection: ActivityDetailSection
  sectionIndex?: number
  selectedUserId?: string
  groupMembers?: GroupMember[]
  relation?: ActivityDetailRelation
}>()

const emit = defineEmits<{ back: [] }>()

const { t } = useI18n()

function handleBack() {
  emit('back')
}

function isTaskRelation(relation: ActivityDetailRelation | undefined): relation is ContentDetailsTest {
  return relation !== null && relation !== undefined && 'testActivities' in relation
}

function isAssignmentRelation(relation: ActivityDetailRelation | undefined): relation is ContentDetailsAssignment {
  return relation !== null && relation !== undefined && 'assignmentKey' in relation
}
</script>

<template>
  <div>
    <div class="mb-3 sm:mb-4">
      <Button
        variant="solid"
        shade="dark"
        color="green"
        class="!bg-green-40 hover:!bg-green-40/90"
        :data-pendo="PendoFeature.ActivityReports.BackToActivity"
        @click="handleBack"
      >
        <FontAwesomeIcon
          :icon="faArrowLeft"
          class="mr-2 h-4 w-4"
          aria-hidden="true"
        />
        {{ t('back') }}
      </Button>
    </div>

    <hr class="border-green-40 mb-6">

    <h2 class="text-green-40 mb-2 text-2xl font-bold">
      {{ content.title }}
    </h2>

    <TaskDetail
      v-if="activeSection === 'task' && isTaskRelation(relation)"
      :content="content"
      :visit="visit"
      :filters="filters"
      :selected-user-id="selectedUserId"
      :relation="relation"
    />

    <AssignmentDetail
      v-else-if="activeSection === 'assignment' && isAssignmentRelation(relation)"
      :content="content"
      :visit="visit"
      :group-members="groupMembers"
      :selected-user-id="selectedUserId"
      :relation="relation"
    />
  </div>
</template>
